import { cloneElement, type JSX, type ReactNode, useRef } from "react";
import { useClickedOutsideElement, useToggle } from "src/hooks";
import { cn } from "src/utilities/shadcnUtils";

export interface DropdownProps {
  trigger: JSX.Element;
  children: ReactNode;
  align?: "left" | "right"; // alignment of the dropdown relative to the trigger component
  closeOnClickOutside?: boolean; // whether to close the dropdown when clicking outside of it, defaults to true
  className?: string;
  triggerClassName?: string;
}

export interface DropdownTriggerProps {
  open?: boolean;
  onClick?: () => void;
}

/**
 * A more primitive version of dropdown that is more flexible but requires the manual management of the open state.
 * If you don't need to manually manage the open state, use the Dropdown component instead.
 */
export const ControlledDropdown = ({
  trigger,
  children,
  open,
  align,
  onClickOutside,
  className,
  triggerClassName,
}: DropdownProps & { open: boolean; onClickOutside?: () => void }) => {
  const ref = useRef(null);
  useClickedOutsideElement(ref, onClickOutside);

  return (
    <div className={cn("relative", triggerClassName)} ref={ref}>
      {trigger}
      {open && (
        <div
          className={cn(
            "absolute z-50 translate-y-2 rounded-md border border-border bg-popover text-popover-foreground shadow-lg dark:shadow-black/40",
            align === "right" && "right-0",
            className
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * A generic dropdown container that can be used to render a dropdown menu or any other types of popup content.
 *
 * @param trigger A component that will be used to trigger the dropdown opening. It's also what the dropdown will be
 * anchored to. The trigger component should accept an "onClick" prop. The component will also receive an "open" prop, which
 * can optionally be used to do conditional styling or trigger other behaviour
 */

const Dropdown = ({
  trigger,
  children,
  closeOnClickOutside = true,
  triggerClassName,
  ...rest
}: DropdownProps) => {
  const toggle = useToggle();
  return (
    <ControlledDropdown
      open={toggle.on}
      triggerClassName={triggerClassName}
      onClickOutside={closeOnClickOutside ? toggle.setOff : undefined}
      trigger={cloneElement(trigger, {
        open: toggle.on,
        onClick: toggle.toggle,
      })}
      {...rest}
    >
      {children}
    </ControlledDropdown>
  );
};

export default Dropdown;
